import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { FileSortOrder } from '@/types/node';
import { log } from '@/logger/logger.ts';
import { UndoRedoEntry } from '../undoRedo';
import { registerLocalModelBytes, releaseLocalModelBytes } from '../resourceLoading';
import type { BaseNodeData, NodeViewPreferences } from '@/types/node';
import type { FileKind, NodeId, Web, Edit } from 'web';
import type { FlowGraph } from '../FlowGraph';
import type { ClearableAssetsView } from './NodeAssetManager';

const logger = log.getLogger('BaseFileView');

/** `selectedIndex` after removing one file without changing which asset stays selected when possible. */
export function selectedIndexAfterRemovingFile(
  currentSelectedIndex: number,
  indexToRemove: number,
  fileCount: number,
): number {
  if (indexToRemove < currentSelectedIndex) {
    return currentSelectedIndex - 1;
  }
  if (indexToRemove > currentSelectedIndex) {
    return currentSelectedIndex;
  }
  if (currentSelectedIndex === fileCount - 1 && fileCount > 1) {
    return currentSelectedIndex - 1;
  }
  return currentSelectedIndex;
}

/**
 * Base class for view classes that manage file lists and selected index.
 * Provides common functionality for adding, removing, and managing files.
 */
export abstract class BaseFileView<
  TNodeData extends BaseNodeData & NodeViewPreferences & { selectedIndex: number },
> implements ClearableAssetsView {
  constructor(
    protected graph: FlowGraph,
    protected nodeId: NodeId,
  ) {}

  /**
   * Get the node data. Must be implemented by subclasses.
   */
  abstract getNodeData(): TNodeData;

  /**
   * Get the list of files. Must be implemented by subclasses as the source differs.
   */
  abstract getFilesList(): FileKind[];

  /**
   * Set files in the WASM layer. Implementation differs between subclasses.
   */
  protected abstract wasmSetFiles(wasm: Web, files: FileKind[]): Edit;

  protected ensureInsertionOrders(files: FileKind[]): FileKind[] {
    if (files.every((f) => f.insertionOrder !== undefined)) return files;

    return files.map((file, index) => ({ ...file, insertionOrder: index }));
  }

  private getNextInsertionOrder(files: FileKind[]): number {
    return files.reduce((max, f) => {
      const order: number = f.insertionOrder ?? 0;
      return Math.max(max, order + 1);
    }, 0);
  }

  protected sortFilesByName(files: FileKind[]): FileKind[] {
    const unnamed = files.filter((f) => !f.name);
    const named = files.filter((f) => f.name);
    named.sort((a, b) => (a.name as string).localeCompare(b.name as string, undefined, { numeric: true }));

    return [...unnamed, ...named];
  }

  private sortFilesByDateAdded(files: FileKind[]): FileKind[] {
    const sorted = [...files];
    sorted.sort((a, b) => (a.insertionOrder ?? 0) - (b.insertionOrder ?? 0));

    return sorted;
  }

  addFile(file: FileKind, ongoing = false) {
    if (this.graph.store.getNodeNullable(this.nodeId) === undefined) {
      return;
    }
    const existingFiles = this.ensureInsertionOrders(this.getFilesList());
    const newFile: FileKind = { ...file, insertionOrder: this.getNextInsertionOrder(existingFiles) };
    const newFiles = [...existingFiles, newFile];

    const sortOrder = this.getNodeData().sortOrder;
    if (sortOrder === FileSortOrder.Name) {
      const sorted = this.sortFilesByName(newFiles);
      const newSelectedIndex = sorted.findIndex((f) => f === newFile);
      this.graph.edit(() => this.updateFiles(sorted, newSelectedIndex), ongoing);
    } else {
      this.graph.edit(() => this.updateFiles(newFiles, existingFiles.length), ongoing);
    }
    this.graph.updateNodeOutputs(this.nodeId, ongoing);
    this.graph.loadResources();
  }

  /** Returns an undo entry for removing one file. Caller must wrap in `graph.edit()`. */
  removeFileUndoEntry(indexToRemove: number): UndoRedoEntry | null {
    const filesList = this.getFilesList();
    if (!filesList[indexToRemove]) {
      return null;
    }
    const newSelectedIndex = selectedIndexAfterRemovingFile(this.getSelectedIndex(), indexToRemove, filesList.length);
    const newFiles = filesList.filter((_, index) => index !== indexToRemove);
    return this.updateFiles(newFiles, newSelectedIndex);
  }

  removeFile(indexToRemove: number, ongoing = false) {
    const filesList = this.getFilesList();
    if (!filesList[indexToRemove]) throw new Error('invalid index');
    this.graph.edit(() => this.removeFileUndoEntry(indexToRemove) ?? UndoRedoEntry.empty(), ongoing);
    this.graph.updateNodeOutputs(this.nodeId, ongoing);
    this.graph.loadResources();
  }

  // Direct load step 1: register the dropped bytes and add a file pointing at the
  // temp key so the loader renders it immediately (before the upload finishes).
  addLocalThreeDFile(tempKey: string, bytes: Uint8Array, name?: string) {
    registerLocalModelBytes(tempKey, bytes);
    const file: FileKind = {
      type: '3D',
      url: tempKey,
      thumbnailUrl: undefined,
      publicId: undefined,
      id: undefined,
      name,
    };
    this.addFile(file, false);
  }

  // Direct load step 2 (upload succeeded): re-key the WASM resource and swap the
  // temp file for the real CDN-backed one in place, then free the local bytes.
  finalizeLocalThreeDFile(tempKey: string, finalFile: FileKind) {
    // Node was deleted mid-upload — nothing to swap, just release the bytes.
    if (this.graph.store.getNodeNullable(this.nodeId) === undefined) {
      releaseLocalModelBytes(tempKey);
      return;
    }

    const renamed = this.graph.wasm.call((wasm) => wasm.renameThreeDModelResource(tempKey, finalFile.url));
    if (renamed === false) {
      // Rare: the background upload resolved before WASM finished parsing the temp
      // `local:` model, so there was nothing to re-key. The node falls back to a full
      // CDN fetch and re-parse instead of reusing the in-memory bytes.
      // nosemgrep: figma.dont-log-filekeys -- `tempKey` is the transient `local:` 3D model URL, not a Figma file key.
      logger.warn(
        `finalizeLocalThreeDFile: temp 3D model "${tempKey}" was not loaded when upload completed; falling back to CDN re-fetch of "${finalFile.url}"`,
      );
    }

    // Temp file was already removed (e.g. user deleted it mid-upload) — nothing to swap.
    const files = this.getFilesList();
    const index = files.findIndex((f) => f.type === '3D' && f.url === tempKey);
    if (index === -1) {
      releaseLocalModelBytes(tempKey);
      return;
    }

    // Keep the slot's position; only swap the temp url for the final asset.
    const merged: FileKind = { ...finalFile, insertionOrder: files[index].insertionOrder };
    const newFiles = files.map((f, i) => (i === index ? merged : f));
    // Coalesce the file-url swap and the WASM resource re-key into a single undo
    // entry, and make the re-key itself undoable. This keeps the renderer's
    // resource key in sync with the file url across undo/redo. Without it, undo
    // would restore the transient `local:` url while the resource stayed under the
    // CDN key — and since the in-memory bytes are freed once parsing completes, the
    // loader could no longer resolve `local:` and the model would blank out.
    this.graph.edit(() => {
      const entry = this.updateFiles(newFiles, index);
      entry.add(this.reverseRenameEntry(tempKey, finalFile.url));
      return entry;
    }, false);
    this.graph.updateNodeOutputs(this.nodeId, false);
    this.graph.loadResources();
    releaseLocalModelBytes(tempKey);
    if (renamed === true) {
      // Happy path: local bytes were reused and re-keyed to the CDN url with no
      // re-download/re-parse. Logged so we can confirm the optimization is hitting.
      // nosemgrep: figma.dont-log-filekeys -- `tempKey` is the transient `local:` 3D model URL, not a Figma file key.
      logger.debug(
        `finalizeLocalThreeDFile: reused local 3D model "${tempKey}" in place; re-keyed to "${finalFile.url}" without re-fetch`,
      );
    }
  }

  // Self-inverting undo step for the WASM resource re-key in
  // `finalizeLocalThreeDFile`: applying it (undo) re-keys `to → from`, and the
  // returned entry (redo) re-keys `from → to`. Keeps the renderer's resource key
  // tracking the file url so undo never requests a freed `local:` key.
  private reverseRenameEntry(from: string, to: string): UndoRedoEntry {
    return UndoRedoEntry.fromCallback(() => {
      this.graph.wasm.call((wasm) => wasm.renameThreeDModelResource(to, from));
      return this.reverseRenameEntry(to, from);
    });
  }

  // Direct load step 2 (upload failed/aborted): roll back by removing the
  // locally-rendered file and freeing its bytes.
  cancelLocalThreeDFile(tempKey: string) {
    releaseLocalModelBytes(tempKey);
    if (this.graph.store.getNodeNullable(this.nodeId) === undefined) {
      return;
    }
    const files = this.getFilesList();
    const index = files.findIndex((f) => f.type === '3D' && f.url === tempKey);
    if (index !== -1) {
      this.removeFile(index, false);
    }
  }

  getSelectedIndex(): number {
    return this.getNodeData().selectedIndex;
  }

  deleteCurrent() {
    this.removeFile(this.getSelectedIndex());
  }

  deleteAllOthers() {
    const files = this.getFilesList();
    const selectedIndex = this.getSelectedIndex();
    const newFiles = files.filter((_, index) => index === selectedIndex);
    this.setFiles(newFiles, 0, false);
  }

  deleteAll() {
    this.setFiles([], 0, false);
  }

  /**
   * Creates an UndoRedoEntry for updating files (core implementation shared by setFiles and clearAllAssets)
   */
  private updateFiles(files: FileKind[], selectedIndex: number | undefined): UndoRedoEntry {
    this.clearPastedAndInitialData();
    const undoRedoEntry = UndoRedoEntry.empty();
    undoRedoEntry.add(this.graph.wasmChange((wasm) => this.wasmSetFiles(wasm, files)));
    if (selectedIndex !== undefined) {
      undoRedoEntry.add(this.graph.wasmChange((wasm) => wasm.setSelectedMedia(this.nodeId, selectedIndex)));
    }
    return undoRedoEntry;
  }

  /**
   * Clears all assets from this view (returns UndoRedoEntry for batching with other operations)
   */
  clearAllAssets(): UndoRedoEntry {
    return this.updateFiles([], 0);
  }

  /**
   * Checks if this view has any assets
   */
  hasAssets(): boolean {
    return this.getFilesList().length > 0;
  }

  /**
   * Clears the currently selected asset (returns UndoRedoEntry for batching)
   */
  clearCurrentAsset(): UndoRedoEntry {
    return this.removeFileUndoEntry(this.getSelectedIndex()) ?? UndoRedoEntry.empty();
  }

  /**
   * Clears all assets except the currently selected one (returns UndoRedoEntry for batching)
   */
  clearOtherAssets(): UndoRedoEntry {
    const files = this.getFilesList();
    const selectedIndex = this.getSelectedIndex();
    const newFiles = files.filter((_, index) => index === selectedIndex);
    return this.updateFiles(newFiles, 0);
  }

  hasMultipleAssets(): boolean {
    return this.getFilesList().length > 1;
  }

  setFiles(files: FileKind[], selectedIndex: number | undefined, ongoing: boolean) {
    this.graph.edit(() => this.updateFiles(files, selectedIndex), ongoing);
    this.graph.updateNodeOutputs(this.nodeId, ongoing);
    this.graph.loadResources();
  }

  setSelectedIndex(selectedIndex: number | undefined, ongoing: boolean) {
    this.graph.wasmEdit((wasm) => {
      const filesLength = this.getFilesList().length;
      return wasm.setSelectedMedia(this.nodeId, selectedIndex ?? (filesLength > 0 ? filesLength - 1 : 0));
    }, ongoing);
    this.graph.updateNodeOutputs(this.nodeId, ongoing);
    this.graph.loadImageResources();
  }

  clearPastedAndInitialData() {
    this.graph.store.updateNodeData<TNodeData>(
      this.nodeId,
      (_) =>
        ({
          pastedData: undefined,
          initialData: undefined,
        }) as Partial<TNodeData>,
    );
  }

  setViewMode(viewMode: NodeViewMode) {
    this.graph.store.updateNodeData<TNodeData>(
      this.nodeId,
      (_) =>
        ({
          viewMode,
        }) as Partial<TNodeData>,
    );
  }

  setSortOrder(sortOrder: FileSortOrder) {
    const files = this.ensureInsertionOrders(this.getFilesList());
    const selectedFile = files[this.getSelectedIndex()];
    const sortedFiles =
      sortOrder === FileSortOrder.Name ? this.sortFilesByName(files) : this.sortFilesByDateAdded(files);
    const newSelectedIndex = selectedFile ? sortedFiles.indexOf(selectedFile) : 0;

    this.graph.edit(() => {
      const entry = this.updateFiles(sortedFiles, newSelectedIndex);
      entry.add(this.graph.store.updateNodeData<TNodeData>(this.nodeId, (_) => ({ sortOrder }) as Partial<TNodeData>));
      return entry;
    }, false);
    this.graph.updateNodeOutputs(this.nodeId, false);
    this.graph.loadResources();
  }
}
